import { computed } from '@angular/core';
import {
  patchState,
  signalStoreFeature,
  type,
  withComputed,
  withMethods,
} from '@ngrx/signals';
import { IndicatorState } from './indicator.state';
import {
  Indicator,
  isIndicator,
  isWegweiserIndicator,
} from '../../models/indicator';
import { StateFilter } from '../crud.state';
import { Category } from '../../models/category';
import { makeUniqueStringArray } from '../../utility/array.utility';
import { units } from '../../models/unit';
import { WegweiserIndicator } from '../../services/grid-widget-config.service';

/**
 * Generates a title match string based on the provided item, its parent, and optionally grouped children.
 *
 * This function constructs a string composed of the item's name, key, and any parent or grouped children related
 * to the item. The format of the resulting string depends on the relationships present in the data:
 * - If the item has a parent (retrieved from the `all` parameter), the string will include both the item's
 *   and parent's details.
 * - If the item is marked as a set (via `isSet`) and grouped children are provided in `parentGrouped`,
 *   the string will include details of the grouped children.
 * - Otherwise, the string will only include the item's name and key.
 *
 * @param {Indicator} item - The primary item whose details are being used to generate the match string.
 * @param {Record<string, Indicator>} [all] - An optional collection of all items, indexed by their parent keys.
 * @param {Record<string, Indicator>} [parentGrouped] - An optional collection of grouped children,
 *     typically indexed by the parent item's key or identifier.
 * @returns {string} A string constructed from the item's details and its relations, if any.
 */
const getTitleMatchString = (
  item: Indicator,
  all?: Record<string, Indicator>,
  parentGrouped?: Record<string, Indicator[]>
) => {
  const parent =
    item.parent && all && all[item.parent] ? all[item.parent] : null;

  if (parent) {
    return `${item.name}#${parent.name}#${item.key}#${parent.key}`;
  }

  if (item.isSet && parentGrouped && parentGrouped[item._id]) {
    const children = parentGrouped[item._id]
      .map(i => `${item.name}: ${i.name}#${i.key}`)
      .join('#');

    return `${item.name}#${item.key}#${children}`;
  }

  return `${item.name}#${item.key}`;
};

/**
 * Filters a list of indicators based on various criteria such as title, category, tags, products, locked status, and parent relationship.
 *
 * @param {Indicator[]} items - The list of indicators to be filtered.
 * @param {StateFilter} filter - The filter criteria used to determine which indicators match.
 *                               This object may include parameters such as query, locked status,
 *                               category, tags, products, and specific IDs.
 * @param {('' | 'all' | 'noSets' | string)} [parent] - Optional filter to match indicators based on their parent relationship.
 *                                                      - '' or undefined: No parent filtering.
 *                                                      - 'all': Matches all non-set indicators.
 *                                                      - 'noSets': Matches all indicators with no parent.
 *                                                      - Any string: Matches indicators whose parent matches the provided string.
 * @param {Record<string, Indicator>} [all] - An optional mapping of all available indicators, used to resolve parent names
 *                                            when filtering based on the query.
 * @param {boolean} [pool=false] - Determines whether to include/exclude indicators that belong to a category,
 *                                 based on the `pool` property of the indicator. If true, filters out indicators
 *                                 from the pool category.
 *
 * @returns {Indicator[]} A filtered list of indicators that match the given criteria.
 */
const filterItems = (
  items: Indicator[],
  filter: StateFilter,
  parent?: '' | 'all' | 'noSets' | string,
  all?: Record<string, Indicator>,
  pool = false,
  parentGrouped?: Record<string, Indicator[]>
): Indicator[] => {
  return items.filter(item => {
    const titleMatch = getTitleMatchString(item, all, parentGrouped)
      .toLowerCase()
      .includes(filter.query.toLowerCase());
    const lockedMatch = filter.locked === item.locked || filter.locked === null;
    const categoryMatch =
      filter.category.includes(item.category) || filter.category.length === 0;
    const tagsMatch =
      !filter.tags ||
      filter.tags.length === 0 ||
      (item.tags &&
        filter.tags.some(tagId => item.tags && item.tags.includes(tagId)));
    const productsMatch =
      !filter.products ||
      filter.products.length === 0 ||
      (item.products &&
        filter.products.some(
          tagId => item.products && item.products.includes(tagId)
        ));
    const idMatch =
      !filter.ids || filter.ids.length === 0 || filter.ids.includes(item._id);

    let parentMatch: boolean;
    if (parent === 'all') {
      parentMatch = !item.isSet;
    } else if (parent === 'noSets') {
      parentMatch = !item.parent;
    } else if (parent) {
      parentMatch = item.parent === parent;
    } else {
      parentMatch = true;
    }

    const poolMatch = pool ? !!item.category && !item.pool : true;
    //const parentIdMatch = !parent ? !item.parent : item.parent === parent;

    return (
      titleMatch &&
      categoryMatch &&
      lockedMatch &&
      tagsMatch &&
      productsMatch &&
      idMatch &&
      parentMatch &&
      poolMatch
    );
  });
};

/**
 * Builds and returns a sorted list of indicators based on their categories, hierarchical parent-child relationships, and sorting order.
 *
 * @param {Category[]} categories - The list of categories to organize the indicators.
 * @param {Indicator[]} indicators - The primary list of indicators to be sorted.
 * @param {{ [parent: string]: Indicator[] }} groupedChildren - A map where each parent ID associates with its child indicators.
 * @param {Indicator[]} allParents - A list of all potential parent indicators used to ensure completeness.
 * @return {Indicator[]} A sorted array of indicators ordered by their category, parent-child relationships, and sorting order.
 */
export function buildSortedIndicatorList(
  categories: Category[],
  indicators: Indicator[],
  groupedChildren: { [parent: string]: Indicator[] },
  allParents: Indicator[]
): Indicator[] {
  const sortedIndicators: Indicator[] = [];

  // add missing parents
  const parentsToAdd = allParents.filter(
    item =>
      item.isSet &&
      indicators.some((child: Indicator) => child.parent === item._id)
  );

  // Combine the filtered items with their parents, ensuring no duplicates
  indicators = Array.from(new Set([...indicators, ...parentsToAdd]));

  function addIndicatorsFromCategory(categoryId: string) {
    indicators
      .filter(
        indicator => indicator.category === categoryId && !indicator.parent
      )
      .sort((a, b) => a.sorting - b.sorting)
      .forEach(indicator => {
        sortedIndicators.push(indicator);
        if (indicator.isSet && groupedChildren[indicator._id]) {
          sortedIndicators.push(
            ...groupedChildren[indicator._id].filter(i =>
              indicators.includes(i)
            )
          );
        }
      });

    categories
      .filter(category => category.parent === categoryId)
      .sort((a, b) => a.sorting - b.sorting)
      .forEach(subCategory => addIndicatorsFromCategory(subCategory._id));
  }

  // Start from top-level categories (those without a parent)
  categories
    .filter(category => !category.parent)
    .sort((a, b) => a.sorting - b.sorting)
    .forEach(category => addIndicatorsFromCategory(category._id));

  return sortedIndicators;
}

export function withIndicatorSelectors() {
  return signalStoreFeature(
    {
      state: type<IndicatorState>(),
    },
    withComputed(({ items, selectedId, selection, filter, pool }) => ({
      countWithoutSets: computed(
        () => Object.values(items()).filter(item => !item.isSet).length
      ),
      withoutSets: computed(() =>
        Object.values(items()).filter(item => !item.isSet)
      ),
      asArray: computed(() => Object.values(items())),
      filteredItems: computed(() => {
        const all = items();
        let filtered = Object.values(all);
        if (filter()) {
          filtered = filterItems(filtered, filter(), undefined, items());
        }
        return filtered as Indicator[];
      }),
      filteredSelection: computed(() => {
        let filtered = selection();
        if (filter()) {
          filtered = filterItems(filtered, filter());
        }
        return filtered as Indicator[];
      }),
      poolIndicators: computed(() => {
        if (!pool) return {};
        const poolIndicators = pool();
        const indicators: { [key: string]: Indicator } = {};
        for (const idx in poolIndicators) {
          const item = poolIndicators[idx];
          indicators[item._id] = item;
        }
        return indicators;
      }),
      poolFiltered: computed(() => {
        if (!pool) return [];
        let filtered = pool();
        if (filter()) {
          filtered = filterItems(filtered, filter(), 'noSets') as Indicator[];
        }
        const allKeys = Object.values(items()).map(
          (item: Indicator) => item.key
        );

        const result = filtered.filter(
          (item: Indicator) => allKeys.indexOf(item.key) === -1
        );

        return result;
      }),
      onlySets: computed(() => {
        const filtered = Object.values(items()).filter(indicator => {
          return indicator.isSet;
        });
        return filtered.sort((a, b) => (a.name < b.name ? -1 : 1));
      }),
      allKeys: computed(() => {
        return Object.values(items()).map((item: Indicator) => {
          return item.key;
        });
      }),
      keyIdList: computed(() => {
        const keyIdList: { [key: string]: string } = {};
        for (const id in items()) {
          const item = items()[id];
          keyIdList[item.key] = id;
        }
        return keyIdList;
      }),
      poolKeyIdList: computed(() => {
        if (!pool) return {};
        const poolIndicators = pool();

        const keyIdList: { [key: string]: string } = {};
        for (const idx in poolIndicators) {
          const item = poolIndicators[idx];
          keyIdList[item.key] = item._id;
        }

        return keyIdList;
      }),
      groupedByCategory: computed(() => {
        //const grouped = Object.values(items()).reduce(
        const filtered = Object.values(items());
        const grouped = filterItems(filtered, filter()).reduce(
          (acc, indicator) => {
            const { category } = indicator;
            if (!acc[category]) acc[category] = [];
            acc[category].push(indicator);
            return acc;
          },
          {} as Record<string, Indicator[]>
        );

        // Sort each group
        Object.values(grouped).forEach(group =>
          group.sort((a, b) => a.sorting - b.sorting)
        );

        return grouped;
      }),
      groupedChildren: computed(() => {
        const filtered = Object.values(items());
        const grouped = filterItems(filtered, filter()).reduce(
          (acc, indicator) => {
            const { parent } = indicator;
            if (!parent) return acc;
            if (!acc[parent]) acc[parent] = [];
            acc[parent].push(indicator);
            return acc;
          },
          {} as Record<string, Indicator[]>
        );

        // Sort each group
        Object.values(grouped).forEach(group =>
          group.sort((a, b) => a.sorting - b.sorting)
        );

        return grouped;
      }),
      childrenFromSelected: computed(() => {
        if (!selectedId()) return [];
        return Object.values(items())
          .filter((item: Indicator) => {
            return item.parent === items()[selectedId()]._id;
          })
          .sort((a, b) => a.sorting - b.sorting);
      }),
      sortedByCategoryAndName: computed(() => {
        const allIndicators = Object.values(items());

        // Grouping children by parent
        const groupedChildren = allIndicators.reduce(
          (acc, indicator) => {
            if (indicator.parent) {
              if (!acc[indicator.parent]) acc[indicator.parent] = [];
              acc[indicator.parent].push(indicator);
            }
            return acc;
          },
          {} as Record<string, Indicator[]>
        );

        // Helper function to add parents and their children recursively
        function addChildrenSorted(
          parent: Indicator,
          resultList: Indicator[],
          groupedChildrenMap: Record<string, Indicator[]>
        ) {
          resultList.push(parent);
          const children = groupedChildrenMap[parent._id] || [];
          children.sort((a, b) => a.sorting - b.sorting);
          children.forEach(child =>
            addChildrenSorted(child, resultList, groupedChildrenMap)
          );
        }

        // Sorting indicators by category and name
        const sortedByCategory: Record<string, Indicator[]> = allIndicators
          .filter(indicator => !indicator.parent) // Start with root indicators
          .reduce(
            (acc, indicator) => {
              if (!acc[indicator.category]) acc[indicator.category] = [];
              acc[indicator.category].push(indicator);
              return acc;
            },
            {} as Record<string, Indicator[]>
          );

        // Final sorted list
        const sortedList: Indicator[] = [];

        Object.keys(sortedByCategory)
          .sort()
          .forEach(categoryKey => {
            const categoryIndicators = sortedByCategory[categoryKey];

            categoryIndicators.sort((a, b) => a.sorting - b.sorting);
            categoryIndicators.forEach(indicator =>
              addChildrenSorted(indicator, sortedList, groupedChildren)
            );
          });

        return sortedList;
      }),
    })),
    withMethods(store => ({
      globalSearch(
        query: string,
        addChildren = false,
        tags?: string[]
      ): Indicator[] {
        const all = store.items();
        const itemsArray = Object.values(all);
        const tempFilter: StateFilter = { ...store.filter(), query, tags };

        return filterItems(
          itemsArray,
          tempFilter,
          addChildren ? 'all' : '',
          all,
          true,
          store.groupedChildren()
        ).sort((a, b) => {
          if (a.parent === b.parent) {
            // If they have the same parent (including null), sort by name
            return a.name.localeCompare(b.name);
          } else if (!a.parent) {
            // If `a` has no parent, it should come first
            return -1;
          } else if (!b.parent) {
            // If `b` has no parent, it should come first
            return 1;
          } else {
            // Otherwise, sort by parent value
            return a.parent.localeCompare(b.parent);
          }
        });
      },
      getByIds(ids: string[]): Indicator[] {
        return ids.map(id => store.items()[id]).filter(item => !!item);
      },
      filterByParent(parentId: string, fromPool = false) {
        const itemsArray =
          store.pool && fromPool ? store.pool() : Object.values(store.items());
        const tempFilter: StateFilter = { ...store.filter() };
        return filterItems(itemsArray, tempFilter, parentId).sort(
          (a, b) => a.sorting - b.sorting
        ) as Indicator[];
      },
      getParent(indicatorId: string) {
        const indicator = store.items()[indicatorId];
        if (indicator && indicator.parent) {
          const parent = store.items()[indicator.parent];
          return parent || null;
        }
        return null;
      },
      getChildren(indicatorId: string): Indicator[] {
        return store.groupedChildren()[indicatorId] || [];
      },
      getByCategoryId(categoryId: string) {
        return store.groupedByCategory()[categoryId];
      },
      addParentsToSelection() {
        const selection: Indicator[] = store.selection();
        const allParentsSet = new Set<string>(selection.map(i => i._id));
        for (const indicator of selection) {
          const parent = this.getParent(indicator._id);
          if (!parent) continue;
          allParentsSet.add(parent._id);
        }

        const items = store.items();

        // Map back the IDs to full Category objects
        const newSelection = Array.from(allParentsSet).map(id => items[id]);

        patchState(store, { selection: newSelection });
      },
      addChildrenToSelection() {
        const selection: Indicator[] = store.selection();
        const allChildrenSet = new Set<string>(selection.map(i => i._id));
        for (const indicator of selection) {
          const children = this.getChildren(indicator._id);
          children.forEach(child => allChildrenSet.add(child._id));
        }

        const items = store.items();

        // Map back the IDs to full Category objects
        const newSelection = Array.from(allChildrenSet).map(id => items[id]);

        patchState(store, { selection: newSelection });
      },
      getFullName(
        indicator: Indicator | WegweiserIndicator,
        addUnitSuffix = true,
        addParentPrefix = true
      ) {
        if (!indicator) return '';
        if (isWegweiserIndicator(indicator))
          return `${indicator.name} ${addUnitSuffix ? `(${indicator.unit})` : ''}`;

        let name = '';
        if (indicator.parent && addParentPrefix) {
          const parent = this.getParent(indicator._id);
          name = parent ? `${parent.name}: ${indicator.name}` : indicator.name;
        } else {
          name = indicator.name;
        }
        return addUnitSuffix ? `${name} ${units[indicator.unit]?.short}` : name;
      },

      getFullNameById(
        indicatorId: string,
        addUnitSuffix = true,
        addParentPrefix = true
      ) {
        return this.getFullName(
          store.items()[indicatorId],
          addUnitSuffix,
          addParentPrefix
        );
      },
      getScales(indicator: Indicator) {
        if (!indicator) return undefined;
        const legend = indicator.legend;
        const colorset = indicator.defaultColorSet;

        if (
          indicator.legendMode === 'fixed' &&
          legend &&
          legend.length > 0 &&
          colorset
        )
          return legend;
        if (indicator.parent) {
          const parent = this.getParent(indicator._id);
          return parent &&
            parent.legendMode === 'fixed' &&
            parent.legend &&
            parent.legend.length &&
            parent.defaultColorSet
            ? parent.legend
            : undefined;
        }
        return undefined;
      },
      getSetIdsByIds(ids: string[]) {
        const all = store.items();
        const setIds: string[] = [];
        ids.forEach(id => {
          const indicator = all[id];
          if (indicator && indicator.parent) setIds.push(indicator.parent);
        });
        return makeUniqueStringArray(setIds);
      },
      categoriesHaveIndicators(categoryIds: string[]) {
        return categoryIds.some(id => !!store.groupedByCategory()[id]);
      },
    }))
  );
}
